import React, { useState } from 'react';
import PropTypes from 'prop-types';

const MetricsExplanation = ({
	showAdjustedKPI = false,
	qualityWeight = 60,
	timeWeight = 25,
	productivityWeight = 15,
	hasDesigners = false,
	hasNonDesigners = true,
}) => {
	const [isOpen, setIsOpen] = useState(false);

	// Determine quality label based on audience mix
	const qualityLabel = hasDesigners && hasNonDesigners
		? 'QA / Design Points'
		: hasDesigners
			? 'Design Points'
			: 'QA Points';

	const qualityDescription = hasDesigners && hasNonDesigners
		? 'Work quality points: QA review points for developers, Design Points for designers.'
		: hasDesigners
			? 'Design quality based on Design Points assigned during design review.'
			: 'Code quality based on QA review points assigned during code review.';

	const qualityNote = hasDesigners && hasNonDesigners
		? 'Only tasks with QA or Design reviews are included in the average. Each role uses its own metric.'
		: hasDesigners
			? 'Only tasks with Design reviews are included in the average.'
			: 'Only tasks with QA reviews are included in the average.';

	return (
		<div className='mt-4 border rounded-lg overflow-hidden bg-white'>
			<button
				onClick={() => setIsOpen(!isOpen)}
				className='w-full px-4 py-3 text-left font-semibold bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between'
			>
				<span>📊 Metrics Explanation</span>
				<svg
					className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
					fill='none'
					stroke='currentColor'
					viewBox='0 0 24 24'
				>
					<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
				</svg>
			</button>
			{isOpen && (
				<div className='px-4 py-4 space-y-4 text-sm'>
					<div className='border-b pb-3'>
						<h4 className='font-bold text-base mb-2'>🎯 KPI (Key Performance Indicator)</h4>
						<p className='text-gray-700 mb-2'>
							The <strong>Efficiency Score (KPI)</strong> is a comprehensive metric combining multiple performance aspects:
						</p>
						<ul className='list-disc pl-5 space-y-1 text-gray-600'>
							<li><strong>Quality ({qualityWeight}%)</strong> — {qualityLabel} (work quality score)</li>
							<li><strong>Time ({timeWeight}%)</strong> — Time estimation accuracy (A/E ratio)</li>
							<li><strong>Productivity ({productivityWeight}%)</strong> — Work efficiency (A/S ratio)</li>
						</ul>
						<p className='text-gray-700 mt-2'>
							<strong>Score ranges:</strong> 80+ Excellent • 70+ Good • 60+ Average • 50+ Below Average • &lt;50 Poor
						</p>
					</div>

					<div className='border-b pb-3'>
						<h4 className='font-bold text-base mb-2'>✨ Quality Score ({qualityWeight}%)</h4>
						<p className='text-gray-700 mb-2'>{qualityDescription}</p>
						<ul className='list-disc pl-5 space-y-1 text-gray-600'>
							<li><strong>Scale:</strong> 0–100 points</li>
							<li><strong>Impact:</strong> Higher points = better quality = higher KPI</li>
							<li><strong>Note:</strong> {qualityNote}</li>
						</ul>
						<p className='text-gray-700 mt-2 italic'>
							This is the most important factor as it reflects the quality of work delivered.
						</p>
					</div>

					<div className='border-b pb-3'>
						<h4 className='font-bold text-base mb-2'>⏱️ Time Score ({timeWeight}%)</h4>
						<p className='text-gray-700 mb-2'>
							Evaluates time estimation accuracy based on the <strong>A/E ratio</strong> (Actual / Estimated hours).
						</p>
						<ul className='list-disc pl-5 space-y-1 text-gray-600'>
							<li><strong>Ideal A/E:</strong> 1.0 (perfect estimation) = 100 points</li>
							<li><strong>Formula:</strong> Score = (2 − A/E) × 100, capped at 0–100</li>
							<li><strong>Examples:</strong> A/E=0.8 → 100 • A/E=1.0 → 100 • A/E=1.5 → 50</li>
						</ul>
						<p className='text-gray-700 mt-2 italic'>
							Better estimation skills lead to more predictable project timelines.
						</p>
					</div>

					<div className='border-b pb-3'>
						<h4 className='font-bold text-base mb-2'>🚀 Productivity Score ({productivityWeight}%)</h4>
						<p className='text-gray-700 mb-2'>
							Measures work efficiency based on the <strong>A/S ratio</strong> (Actual hours / Story Points).
						</p>
						<ul className='list-disc pl-5 space-y-1 text-gray-600'>
							<li><strong>Ideal A/S:</strong> ≈2.0 hours per story point = 100 points</li>
							<li><strong>Formula:</strong> Score = (2.0 / A/S) × 100, capped at 0–100</li>
							<li><strong>Examples:</strong> A/S=1.5 → 100 • A/S=2.0 → 100 • A/S=3.0 → 67</li>
						</ul>
						<p className='text-gray-700 mt-2 italic'>
							Lower A/S means more story points completed per hour — higher productivity.
						</p>
					</div>

					<div className='border-b pb-3'>
						<h4 className='font-bold text-base mb-2'>🔁 Reopened Issues</h4>
						<p className='text-gray-700 mb-2'>
							Tasks marked <strong>Reopened</strong> (work that was returned and had to be
							redone) apply a penalty to the KPI.
						</p>
						<ul className='list-disc pl-5 space-y-1 text-gray-600'>
							<li><strong>Trigger:</strong> reopened tasks make up 30% or more of the developer&apos;s tasks in the period</li>
							<li><strong>Penalty:</strong> scales from −10% KPI at 30% up to −30% KPI when every task is reopened</li>
							<li><strong>Below 30%:</strong> no effect on the score</li>
						</ul>
						<p className='text-gray-700 mt-2 italic'>
							A high share of reopened work signals quality problems and lowers the score.
						</p>
					</div>

					<div className='border-b pb-3'>
						<h4 className='font-bold text-base mb-2'>📈 Other Metrics</h4>
						<div className='space-y-2'>
							<div>
								<p className='font-semibold text-gray-800'>Est. (h) — Estimated Hours</p>
								<p className='text-gray-600'>Total estimated hours for all tasks in the selected period.</p>
							</div>
							<div>
								<p className='font-semibold text-gray-800'>P. Act. (h) — Period Actual Hours</p>
								<p className='text-gray-600'>Actual hours tracked only within the selected date range.</p>
							</div>
							<div>
								<p className='font-semibold text-gray-800'>Total Act. (h) — Total Actual Hours</p>
								<p className='text-gray-600'>Actual hours including 3 months before the selected period. Used for A/E and A/S calculations.</p>
							</div>
							<div>
								<p className='font-semibold text-gray-800'>SP — Story Points</p>
								<p className='text-gray-600'>Total story points assigned to tasks, reflecting task complexity and size.</p>
							</div>
							<div>
								<p className='font-semibold text-gray-800'>Comp. — Completed Tasks</p>
								<p className='text-gray-600'>Number of tasks marked as completed in the selected period.</p>
							</div>
						</div>
					</div>

					{showAdjustedKPI && (
						<div>
							<h4 className='font-bold text-base mb-2'>💰 Adj. KPI — Salary-Adjusted KPI</h4>
							<p className='text-gray-700 mb-2'>
								KPI adjusted based on the employee's salary relative to the department average.
							</p>
							<ul className='list-disc pl-5 space-y-1 text-gray-600'>
								<li><strong>Formula:</strong> KPI × (2 − salary_coefficient), capped at ±30%</li>
								<li><strong>Higher salaries:</strong> Stricter expectations (up to −30%)</li>
								<li><strong>Lower salaries:</strong> More lenient evaluation (up to +30%)</li>
							</ul>
							<p className='text-gray-700 mt-2 italic'>
								Ensures fair performance evaluation considering compensation levels.
							</p>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

MetricsExplanation.propTypes = {
	showAdjustedKPI: PropTypes.bool,
	qualityWeight: PropTypes.number,
	timeWeight: PropTypes.number,
	productivityWeight: PropTypes.number,
	hasDesigners: PropTypes.bool,
	hasNonDesigners: PropTypes.bool,
};

export default MetricsExplanation;
